import { useTranslation } from 'react-i18next';

import { useFormatters } from '@/shared/i18n/format';
import type { SearchResult, SearchResultHighlights } from '@/shared/registry/types';
import { Badge } from '@/shared/ui/badge';

import { channelLabel, isChannel, searchStatus, statusVariant } from '../lib/format';

import { ChannelIcon } from './ChannelIcon';

/**
 * A dialog in the global search results: channel tile, client and line, its state and date (a returning client's
 * dialogs are told apart), the matching text.
 */
export function OpenlinesSearchResult({
  result,
  highlights,
}: {
  result: SearchResult;
  highlights?: SearchResultHighlights;
}) {
  const { t } = useTranslation('openlines');
  const fmt = useFormatters();
  const channel = result.meta?.channel ?? '';
  const status = searchStatus(result.meta?.status);
  const when = result.meta?.closedAt ?? result.meta?.createdAt;
  return (
    <>
      {isChannel(channel) ? (
        <ChannelIcon channel={channel} size="md" />
      ) : (
        <span className="size-7 shrink-0 rounded-md bg-muted" aria-hidden />
      )}
      <span className="min-w-0 flex-1" data-testid="openlines-search-result">
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate text-[0.9375rem] font-medium text-foreground">
            {result.meta?.anonymous === 'true' && `${t('search.client')} `}
            {highlights?.title ?? result.title}
          </span>
          {status && (
            <Badge variant={statusVariant(status)} className="shrink-0 px-1.5 py-0 text-[0.625rem]" data-testid="search-status">
              {t(`statuses.${status}`)}
            </Badge>
          )}
          {when && (
            <time dateTime={when} className="ml-auto shrink-0 text-xs text-subtle-foreground tabular-nums">
              {fmt.date(when, 'dateTimeShort')}
            </time>
          )}
        </span>
        <span className="block truncate text-sm text-muted-foreground">
          {t('search.dialog', { channel: channelLabel(channel, t) })}
          {result.snippet && (
            <>
              {' · '}
              {highlights?.snippet ?? result.snippet}
            </>
          )}
        </span>
      </span>
    </>
  );
}
